/* eslint-disable */
import { useEffect, useMemo, useRef, useState } from "react";
import { RefreshCw, Search, SlidersHorizontal } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import NoAccess from "../components/NoAccess";
import DiscordServerCard from "../components/DiscordServerCard";
import DiscordResolverPanel from "../components/DiscordResolverPanel";
import DiscordMarketView from "../components/DiscordMarketView";
import DiscordTierView from "../components/DiscordTierView";
import {
  DISCORD_SORT_OPTIONS,
  fetchDiscordServers,
  fetchDiscordServersBulk,
  refreshDiscordServer,
  updateDiscordServer,
} from "./discordApi";
import "./discord.css";

const PAGE_SIZE = 60;

// Market Mind packs a blob, so a deep tail adds nothing; the Tier Map needs the
// whole set or its smallest buckets look empty.
const MARKET_MAX = 1500;
const TIER_MAX = 8000;

const QUICK_FILTERS = [
  { value: "unreviewed", label: "Unreviewed", params: { reviewed: "false" } },
  { value: "reviewed", label: "Reviewed", params: { reviewed: "true" } },
  { value: "all", label: "All", params: {} },
  { value: "visible", label: "Visible", params: { visible: "true" } },
  { value: "hidden", label: "Hidden", params: { visible: "false" } },
  { value: "tracking", label: "Tracking", params: { tracking: "true" } },
  { value: "tracking_off", label: "Tracking Disabled", params: { tracking: "false" } },
  { value: "verified", label: "Verified", params: { verified: "true" } },
  { value: "partnered", label: "Partnered", params: { partnered: "true" } },
  { value: "failed", label: "Failed / 404", params: { failed: "true" } },
];

export default function DiscordAdmin() {
  const { currentUser, loadingUser } = useAuth();

  // Default workflow: review the most important (highest-member) unreviewed servers first.
  const [quickFilter, setQuickFilter] = useState("unreviewed");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("members");
  const [order, setOrder] = useState("desc");
  const [robloxFilter, setRobloxFilter] = useState(""); // "" | "only" | "hide"

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [toggleError, setToggleError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [busyField, setBusyField] = useState(null);
  const [importSuccess, setImportSuccess] = useState("");
  const [refreshingId, setRefreshingId] = useState(null);

  const [viewMode, setViewMode] = useState("cards");
  const [bubbleItems, setBubbleItems] = useState([]);
  const [bubbleLoading, setBubbleLoading] = useState(false);

  const requestIdRef = useRef(0);
  const bubbleRequestIdRef = useRef(0);
  const isAdmin = !!currentUser?.is_admin;

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const activeQuickFilter = useMemo(
    () => QUICK_FILTERS.find((f) => f.value === quickFilter) || QUICK_FILTERS[0],
    [quickFilter]
  );

  const queryBase = useMemo(
    () => ({
      search: search || undefined,
      sort,
      order,
      limit: PAGE_SIZE,
      roblox: robloxFilter === "only" ? "true" : robloxFilter === "hide" ? "false" : undefined,
      ...activeQuickFilter.params,
    }),
    [search, sort, order, activeQuickFilter, robloxFilter]
  );

  const loadFirstPage = () => {
    const myRequest = ++requestIdRef.current;
    setLoading(true);
    setError("");

    fetchDiscordServers({ ...queryBase, offset: 0 })
      .then((data) => {
        if (requestIdRef.current !== myRequest) return;
        setItems(Array.isArray(data.items) ? data.items : []);
        setTotal(Number(data.total) || 0);
      })
      .catch((err) => {
        if (requestIdRef.current !== myRequest) return;
        setError(String(err.message || err));
        setItems([]);
        setTotal(0);
      })
      .finally(() => {
        if (requestIdRef.current !== myRequest) return;
        setLoading(false);
      });
  };

  useEffect(() => {
    if (!isAdmin) return;
    loadFirstPage();
  }, [queryBase, isAdmin]);

  // The bubble layout needs the whole filtered set, so it loads separately.
  // It shares every card filter but never shows servers hidden from the frontend.
  useEffect(() => {
    if (!isAdmin || viewMode === "cards") return;
    const myRequest = ++bubbleRequestIdRef.current;

    // "Hidden" can never intersect with visible-only, so skip the round trip.
    if (quickFilter === "hidden") {
      setBubbleItems([]);
      setBubbleLoading(false);
      return;
    }

    setBubbleLoading(true);
    const { limit, ...rest } = queryBase;
    fetchDiscordServersBulk(
      { ...rest, visible: "true" },
      viewMode === "tier" ? TIER_MAX : MARKET_MAX
    )
      .then((data) => {
        if (bubbleRequestIdRef.current !== myRequest) return;
        setBubbleItems(data.items);
      })
      .catch((err) => {
        if (bubbleRequestIdRef.current !== myRequest) return;
        setError(String(err.message || err));
        setBubbleItems([]);
      })
      .finally(() => {
        if (bubbleRequestIdRef.current === myRequest) setBubbleLoading(false);
      });
  }, [viewMode, queryBase, isAdmin, quickFilter]);

  const handleLoadMore = async () => {
    const myRequest = ++requestIdRef.current;
    setLoadingMore(true);
    try {
      const data = await fetchDiscordServers({ ...queryBase, offset: items.length });
      if (requestIdRef.current !== myRequest) return;
      setItems((prev) => [...prev, ...(Array.isArray(data.items) ? data.items : [])]);
      setTotal(Number(data.total) || 0);
    } catch (err) {
      if (requestIdRef.current !== myRequest) return;
      setError(String(err.message || err));
    } finally {
      if (requestIdRef.current === myRequest) setLoadingMore(false);
    }
  };

  // Toggling a field never removes the card from view; admin keeps reviewing in place.
  const handleToggleField = async (server, field, value) => {
    setBusyId(server.server_id);
    setBusyField(field);
    setToggleError("");
    try {
      const updated = await updateDiscordServer(server.server_id, { [field]: value });
      setItems((prev) =>
        prev.map((s) => (s.server_id === server.server_id ? { ...s, ...updated } : s))
      );
    } catch (err) {
      setToggleError(String(err.message || err));
    } finally {
      setBusyId(null);
      setBusyField(null);
    }
  };

  // Pulls live numbers straight from Discord and writes a new snapshot.
  const handleRefreshServer = async (server) => {
    setRefreshingId(server.server_id);
    setToggleError("");
    try {
      const data = await refreshDiscordServer(server.server_id);
      if (data.server) {
        setItems((prev) =>
          prev.map((s) => (s.server_id === server.server_id ? { ...s, ...data.server } : s))
        );
      }
      if (!data.success) {
        setToggleError(`${server.server_name}: ${data.error || "refresh failed"}`);
      }
    } catch (err) {
      setToggleError(String(err.message || err));
    } finally {
      setRefreshingId(null);
    }
  };

  if (loadingUser) return null;
  if (!isAdmin) return <NoAccess type="403" />;

  const hasMore = items.length < total;

  return (
    <main className="discord-page discord-admin-page">
      <header className="discord-header">
        <div>
          <div className="discord-eyebrow">ADMIN REVIEW</div>
          <h1>Discord Server Review</h1>
          <p>Work through unreviewed servers by member count, then toggle visibility and tracking.</p>
        </div>
        <div className="discord-header-right">
          <div className="discord-count">
            <strong>{total.toLocaleString()}</strong>
            <span>Matching</span>
          </div>
          <div className="discord-view-tabs" role="tablist" aria-label="Discord view">
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === "cards"}
              className={`discord-view-tab${viewMode === "cards" ? " is-active" : ""}`}
              onClick={() => setViewMode("cards")}
            >
              Cards
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === "market"}
              className={`discord-view-tab${viewMode === "market" ? " is-active" : ""}`}
              onClick={() => setViewMode("market")}
            >
              Market Mind
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === "tier"}
              className={`discord-view-tab${viewMode === "tier" ? " is-active" : ""}`}
              onClick={() => setViewMode("tier")}
            >
              Tier Map
            </button>
          </div>
          <DiscordResolverPanel
            onImportSuccess={(msg) => {
              setImportSuccess(msg);
              setTimeout(() => setImportSuccess(""), 6000);
              loadFirstPage();
            }}
          />
          <button type="button" className="discord-admin-link" onClick={loadFirstPage}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </header>

      <div className="discord-quick-filters">
        {QUICK_FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            className={`discord-pill${quickFilter === f.value ? " is-active" : ""}`}
            onClick={() => setQuickFilter(f.value)}
          >
            {f.label}
          </button>
        ))}
        <button
          type="button"
          className={`discord-pill${robloxFilter === "only" ? " is-active" : ""}`}
          onClick={() => setRobloxFilter((v) => (v === "only" ? "" : "only"))}
          title="Name or description contains 'Roblox'"
        >
          Roblox
        </button>
        <button
          type="button"
          className={`discord-pill${robloxFilter === "hide" ? " is-active" : ""}`}
          onClick={() => setRobloxFilter((v) => (v === "hide" ? "" : "hide"))}
          title="Exclude servers whose name or description contains 'Roblox'"
        >
          Hide Roblox
        </button>
      </div>

      <div className="discord-controls">
        <div className="discord-search">
          <Search size={15} />
          <input
            type="text"
            placeholder="Search servers..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>

        <div className="discord-sort">
          <SlidersHorizontal size={14} />
          <select value={sort} onChange={(e) => setSort(e.target.value)}>
            {DISCORD_SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <select value={order} onChange={(e) => setOrder(e.target.value)}>
            <option value="desc">Descending</option>
            <option value="asc">Ascending</option>
          </select>
        </div>
      </div>

      {error && <div className="discord-error">Failed to load Discord servers: {error}</div>}
      {toggleError && <div className="discord-error">Failed to save change: {toggleError}</div>}
      {importSuccess && <div className="discord-success">{importSuccess}</div>}

      {viewMode === "market" ? (
        <DiscordMarketView servers={bubbleItems} loading={bubbleLoading} />
      ) : viewMode === "tier" ? (
        <DiscordTierView servers={bubbleItems} loading={bubbleLoading} />
      ) : loading ? (
        <div className="discord-empty">Loading servers…</div>
      ) : items.length === 0 ? (
        <div className="discord-empty">No Discord servers match this filter.</div>
      ) : (
        <>
          <div className="discord-grid discord-grid-admin">
            {items.map((server) => (
              <DiscordServerCard
                key={server.server_id}
                server={server}
                admin
                busyField={busyId === server.server_id ? busyField : null}
                onToggleField={(field, value) => handleToggleField(server, field, value)}
                onRefresh={() => handleRefreshServer(server)}
                refreshing={refreshingId === server.server_id}
              />
            ))}
          </div>

          {hasMore && (
            <div className="discord-load-more">
              <button type="button" onClick={handleLoadMore} disabled={loadingMore}>
                {loadingMore ? "Loading…" : `Load More (${items.length} of ${total.toLocaleString()})`}
              </button>
            </div>
          )}
        </>
      )}
    </main>
  );
}
